import { Children, cloneElement, isValidElement, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { isNil } from 'lodash';
import { useSnapshot } from 'valtio/react';

import { Form } from '@paid-ui/components/form';
import { LoadingIndicator } from '@paid-ui/components/loading-indicator';
import { Separator } from '@paid-ui/components/separator';
import { BusinessType } from '@paid-ui/enums/business';
import { ContractRole, ContractType } from '@paid-ui/enums/contract';
import { goToStep, setInvitedSignUp, signUpManager } from '@paid-ui/forms/sign-up/models';
import { useToast } from '@paid-ui/ui/toast';

import {
  StepForm,
  StepFormContent,
  StepFormFooter,
  StepFormHeader,
} from '../../components/step-form';
import { useInvitationSearchParams } from '../../hooks/search-params';
import { useSignUpForm } from '../../hooks/sign-up-form';
import { SignUpStep } from '../../models/enums';
import {
  useGetSignUpInvitationForContract,
  useGetSignUpInvitationForGroup,
} from '../../queries/sign-up-invitation';
import { isOwner } from '../../utils/is-owner';

interface Props {
  children?: React.ReactNode;
}

export const SignUpForm: React.FC<Props> = ({ children }) => {
  const toast = useToast();
  const router = useRouter();
  const form = useSignUpForm();
  const formRef = useRef<HTMLFormElement | null>(null);
  const { steps, currentStep, invitedSignUp } = useSnapshot(signUpManager);
  const searchParams = useSearchParams();
  const currentStepAsStr = searchParams.get('step');
  const isMountedRef = useRef(false);
  const { email, contractId, groupId, token, partyType } = useInvitationSearchParams();
  const isInvitedSignUp = contractId ? Boolean(email && token) : Boolean(groupId && email && token);

  const {
    data: signUpInfoWithContract,
    error: error1,
    isSuccess: isSuccess1,
  } = useGetSignUpInvitationForContract();

  const {
    data: signupInfoWithGroup,
    error: error2,
    isSuccess: isSuccess2,
  } = useGetSignUpInvitationForGroup();

  const isSuccess = isInvitedSignUp ? (contractId ? isSuccess1 : isSuccess2) : true;

  const goToSignIn = () => {
    const pathname = '/signin';
    const urlSearchParams = new URLSearchParams(globalThis.location.search);
    urlSearchParams.delete('token');
    urlSearchParams.delete('email');
    urlSearchParams.delete('step');
    urlSearchParams.sort();
    const search = urlSearchParams.toString();
    router.replace({ pathname, search });
  };

  const goToStepWithSearchParams = (step?: SignUpStep | null, replace?: boolean) => {
    const searchParams = new URLSearchParams(globalThis.location.search);

    if (isNil(step) || Number.isNaN(Number(step))) {
      searchParams.delete('step');
    } else {
      searchParams.set('step', step.toString());
    }

    const pathname = '/signup';
    const search = searchParams.toString();

    if (replace) {
      router.replace({ pathname, search });
    } else {
      router.push({ pathname, search });
    }
  };

  const setDefaultBusinessType = (role?: string) => {
    let invitedRole = role;

    if (!invitedRole) {
      const searchParams = new URLSearchParams(globalThis.location.search);
      const contractType = searchParams.get('contractType')?.toUpperCase();
      const isPrimeContract = !contractType || contractType === ContractType.HEAD_CONTRACT;
      const defaultInvitedRole = isPrimeContract ? ContractRole.PRINCIPAL : undefined;
      invitedRole = searchParams.get('invitedRole')?.toUpperCase() ?? defaultInvitedRole;
    }

    switch (invitedRole) {
      case ContractRole.ARCHITECT:
      case ContractRole.SUPERINTENDENT: {
        form.setValue('type', BusinessType.SUPERINTENDENT);
        break;
      }
      case ContractRole.SPONSOR: {
        form.setValue('type', BusinessType.FUNDER);
        break;
      }
      case ContractRole.QUANTITY_SURVEYOR: {
        form.setValue('type', BusinessType.QUANTITY_SURVEYOR);
        break;
      }
      case ContractRole.BUILDER: {
        form.setValue('type', BusinessType.BUILDER);
        break;
      }
      case ContractRole.OFFEREE:
      case ContractRole.OFFEROR:
      case ContractRole.PRINCIPAL: {
        form.setValue('type', BusinessType.OWNER);
        break;
      }
      case ContractRole.SUBCONTRACTOR: {
        form.setValue('type', BusinessType.SUBCONTRACTOR);
        break;
      }
      case ContractRole.SUPPLIER: {
        form.setValue('type', BusinessType.SUPPLIER);
        break;
      }
      default:
    }
  };

  const hideFooter = invitedSignUp;

  useEffect(() => {
    const searchParams = new URLSearchParams(globalThis.location.search);
    const contractType = searchParams.get('contractType')?.toUpperCase();
    const isPrimeContract = !contractType || contractType === ContractType.HEAD_CONTRACT;
    const defaultInvitedRole = isPrimeContract ? ContractRole.PRINCIPAL : undefined;
    const invitedRole = searchParams.get('invitedRole')?.toUpperCase() ?? defaultInvitedRole;
    const isOwnerInvited = searchParams.has('dialog') && isOwner(invitedRole as ContractRole);
    goToStepWithSearchParams(isOwnerInvited ? SignUpStep.OWNER_DETAILS : null, true);
    isMountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) return;
    goToStep(Number(currentStepAsStr));
  }, [currentStepAsStr]);

  useEffect(() => {
    if (!isMountedRef.current) return;

    if (error1) {
      toast.error(error1 instanceof Error ? error1.message : 'Please sign in to continue.');
      goToSignIn();
      return;
    }

    if (!signUpInfoWithContract) return;

    if (!signUpInfoWithContract.signupRequired) {
      toast.error('You have registered, please sign in.');
      goToSignIn();
      return;
    }

    form.setValue('email', signUpInfoWithContract.email ?? '');
    form.setValue('firstName', signUpInfoWithContract.firstName);
    form.setValue('lastName', signUpInfoWithContract.lastName);
    if (partyType) {
      form.setValue('ownerType', partyType);
    }

    setDefaultBusinessType(signUpInfoWithContract.role);
    setInvitedSignUp({
      invited: true,
      invitedRole: signUpInfoWithContract.role,
      contractType: signUpInfoWithContract.contractType,
      groupId: signUpInfoWithContract.groupId,
      partyType: partyType || null,
    });

    if (signupInfoWithGroup?.groupId) {
      goToStepWithSearchParams(SignUpStep.USER_DETAILS, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signUpInfoWithContract, error1]);

  useEffect(() => {
    if (!isMountedRef.current) return;

    if (error2) {
      toast.error(error2 instanceof Error ? error2.message : 'Please sign in to continue.');
      goToSignIn();
      return;
    }

    if (!signupInfoWithGroup) return;

    if (!signupInfoWithGroup.signupRequired) {
      toast.error('You have registered, please sign in.');
      goToSignIn();
      return;
    }

    form.setValue('email', signupInfoWithGroup.email ?? '');
    form.setValue('firstName', signupInfoWithGroup.firstName);
    form.setValue('lastName', signupInfoWithGroup.lastName);
    if (partyType) {
      form.setValue('ownerType', partyType);
    }

    setDefaultBusinessType();
    setInvitedSignUp({
      invited: true,
      groupId: signupInfoWithGroup.groupId,
      partyType: partyType || null,
    });

    if (signupInfoWithGroup.groupId) {
      goToStepWithSearchParams(SignUpStep.USER_DETAILS, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupInfoWithGroup, error2]);

  if (!children) {
    return null;
  }

  if (!isSuccess) {
    return <LoadingIndicator className="h-80" />;
  }

  return (
    <Form {...form}>
      <StepForm
        ref={formRef}
        id="signUpForm"
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
        className="relative flex w-full flex-1 flex-col justify-center space-y-9"
      >
        <StepFormHeader currentStep={currentStep} steps={steps} />
        <StepFormContent>
          {Children.map(children, (child) =>
            isValidElement<{
              disabled?: boolean;
              goToStep: (step?: SignUpStep | null, replace?: boolean) => void;
            }>(child)
              ? cloneElement(child, {
                  ...child.props,
                  disabled: !isSuccess,
                  goToStep: goToStepWithSearchParams,
                })
              : null,
          )}
        </StepFormContent>
      </StepForm>
      {currentStep === SignUpStep.TYPE_AND_EMAIL ? null : (
        <>
          <Separator className="w-full text-grey-hover" hidden={hideFooter} />
          <StepFormFooter hidden={hideFooter} currentStep={currentStep} steps={steps} />
        </>
      )}
    </Form>
  );
};
